import { useI18n } from 'vue-i18n';

import { UnitStatusEnum } from '@/enums/UnitStatus';
import type { Unit } from '@/typings/units/Unit';
import { DefaultUnitsFilters, type UnitsFilters } from '@/typings/units/UnitsFilters';

export function useUnit() {
  const { t } = useI18n();

  function getAllStatuses(): UnitStatusEnum[] {
    return [
      UnitStatusEnum.Upcoming,
      UnitStatusEnum.Available,
      UnitStatusEnum.Sold,
      UnitStatusEnum.Rented,
      UnitStatusEnum.Reserved,
    ];
  }

  function getStatusColor(unitStatus: UnitStatusEnum): string | undefined {
    switch (unitStatus) {
    case UnitStatusEnum.Upcoming:
      return 'blue';
    case UnitStatusEnum.Available:
      return 'green';
    case UnitStatusEnum.Sold:
      return 'red';
    case UnitStatusEnum.Rented:
      return 'red';
    case UnitStatusEnum.Reserved:
      return 'orange';
    default:
      return undefined;
    }
  }

  function getStatusText(unitStatus: UnitStatusEnum): string | undefined {
    switch (unitStatus) {
    case UnitStatusEnum.Upcoming:
      return t('browsingApp.statuses.unitStatus.Upcoming');
    case UnitStatusEnum.Available:
      return t('browsingApp.statuses.unitStatus.Available');
    case UnitStatusEnum.Sold:
      return t('browsingApp.statuses.unitStatus.Sold');
    case UnitStatusEnum.Rented:
      return t('browsingApp.statuses.unitStatus.Rented');
    case UnitStatusEnum.Reserved:
      return t('browsingApp.statuses.unitStatus.Reserved');
    default:
      return undefined;
    }
  }

  function filterUnits(units: Unit[], filters?: UnitsFilters) {
    if (filters === undefined) {
      return units;
    }

    return units.filter((unit: Unit) => {
      if (filters.selectedBedroomsCounts !== undefined 
        && filters.selectedBedroomsCounts.length > 0
        && unit.bedrooms !== undefined
        && !filters.selectedBedroomsCounts.includes(unit.bedrooms)) {
        return false;
      }
      if (filters.selectedFloorNo !== undefined
        && unit.floorNo !== undefined
        && !(filters.selectedFloorNo === unit.floorNo)) {
        return false;
      }
      if (filters.selectedLivingAreaRange !== undefined 
        && filters.selectedLivingAreaRange.min !== undefined 
        && filters.selectedLivingAreaRange.max !== undefined
        && !(unit.livingArea && filters.selectedLivingAreaRange.min <= unit.livingArea && unit.livingArea <= filters.selectedLivingAreaRange.max)) {
        return false;
      }
      if (filters.selectedPriceRange !== undefined 
        && filters.selectedPriceRange.min !== undefined 
        && filters.selectedPriceRange.max !== undefined
        && !(filters.selectedPriceRange.min <= unit.basePrice && unit.basePrice <= filters.selectedPriceRange.max)) {
        return false;
      }
      if (filters.selectedStatuses !== undefined 
        && filters.selectedStatuses.length > 0 
        && !filters.selectedStatuses.includes(unit.status)) {
        return false;
      }
  
      return true;
    });
  }

  function getDefaultUnitFilters(): UnitsFilters {
    return { ...DefaultUnitsFilters };
  }

  function sortUnitsByStatus(units: Unit[]): Unit[] {
    const { locale } = useI18n();
    const sortedUnits = [];
    
    // Tri par nom d'unité pour chaque groupe de statut
    const sortByUnitNumber = (a: Unit, b: Unit) => a.unitNumber.localeCompare(b.unitNumber, locale.value);
    
    // Unités disponibles avec promotions
    sortedUnits.push(...units.filter(
      (u) => u.status === UnitStatusEnum.Available &&
      (u.promotions != null && u.promotions.length > 0 || u.promotionsEn != null && u.promotionsEn.length > 0))
      .sort(sortByUnitNumber));
      
    // Unités disponibles sans promotions
    sortedUnits.push(...units.filter(
      (u) => u.status === UnitStatusEnum.Available &&
      (u.promotions == null || u.promotions.length === 0) && (u.promotionsEn == null || u.promotionsEn.length === 0))
      .sort(sortByUnitNumber));
      
    // Unités réservées
    sortedUnits.push(...units.filter((u) => u.status === UnitStatusEnum.Reserved)
      .sort(sortByUnitNumber));
      
    // Unités vendues
    sortedUnits.push(...units.filter((u) => u.status === UnitStatusEnum.Sold)
      .sort(sortByUnitNumber));
      
    // Unités louées
    sortedUnits.push(...units.filter((u) => u.status === UnitStatusEnum.Rented)
      .sort(sortByUnitNumber));
      
    return sortedUnits;
  }

  return {
    getAllStatuses,
    getStatusColor,
    getStatusText,
    filterUnits,
    getDefaultUnitFilters,
    sortUnitsByStatus,
  };
}
